import { LeadsActionType } from './enums';
import { LeadsAction, LeadsState } from './interfaces';

export const initialState: LeadsState = {
	error: null,
	loading: false,
	leadsList: [],
	leadDetails: {},
	leadProjects: {},
	leadProjectsLoading: false,
	leadProjectsError: {},
	leadDetailsLoading: false,
	leadDetailsError: {},
	totalRecords: 0,
	autoSuggestLeads: [],
	autoSuggestLeadsError: {},
	autoSuggestUsers: [],
	autoSuggestUsersError: {},

	leadQuotationList: [],
	leadQuotationListError: null,
	leadQuotationListLoading: false,

	quotationToolList: [],
	additionalQuotations: [],
	quotationToolListError: null,
	quotationToolListLoading: false,
	quotationToolListLocked: false,
	approvalProducts: [],
	canApproveProducts: false,
	quotationGrandTotal: 0,
	predictionId: '',

	fcBuilderData: {},
	fcBuilderDataLoading: false,

	leadCredits: 0,
	leadCreditsLoading: false,

	leadSourceHistory: [],
	leadSourceHistoryLoading: false,
	leadSourceHistoryError: null,

	leadRemarks: [],
	leadRemarksLoading: false,
	leadRemarksError: null,

	refreshCostingLoading: false,
	refreshCostingStatus: 'IDLE',
	refreshCostingError: null,

	costingSyncStatusLoading: false,
	needsRecalculate: false,
	costingSyncDriftCount: 0,
	costingSyncSkippedCount: 0
};

export default (state = initialState, { type, payload }: LeadsAction): LeadsState => {
	switch (type) {
		case LeadsActionType.FETCH_LEADS: {
			return {
				...state,
				loading: true,
				leadsList: [],
			};
		}

		case LeadsActionType.STORE_LEADS: {
			return {
				...state,
				loading: false,
				leadsList: payload.leadsList,
				totalRecords: payload.totalRecords
			};
		}

		case LeadsActionType.FETCH_LEAD_DETAILS: {
			return {
				...state,
				leadDetailsLoading: true,
				leadDetailsError: {}
			};
		}

		case LeadsActionType.STORE_LEAD_DETAILS: {
			return {
				...state,
				leadDetailsLoading: false,
				leadDetails: payload.data
			};
		}

		case LeadsActionType.ERROR_STORE_LEAD_DETAILS: {
			return {
				...state,
				leadDetailsLoading: false,
				leadDetailsError: payload.error
			};
		}

		case LeadsActionType.FETCH_LEAD_PROJECTS: {
			return {
				...state,
				leadProjectsLoading: true,
				leadProjectsError: {}
			};
		}

		case LeadsActionType.STORE_LEAD_PROJECTS: {
			return {
				...state,
				leadProjectsLoading: false,
				leadProjects: payload?.data
			};
		}

		case LeadsActionType.ERROR_STORE_LEAD_DETAILS: {
			return {
				...state,
				leadProjectsLoading: false,
				leadProjectsError: payload.error
			};
		}

		case LeadsActionType.ERROR_LEADS: {
			return {
				...state,
				loading: false,
				error: payload.error
			};
		}

		case LeadsActionType.STORE_LEAD_AUTOCOMPLETE: {
			return {
				...state,
				autoSuggestLeads: payload.data
			};
		}

		case LeadsActionType.ERROR_FETCH_LEAD_AUTOCOMPLETE: {
			return {
				...state,
				autoSuggestLeadsError: payload.error
			};
		}

		case LeadsActionType.STORE_LEAD_USER_AUTOCOMPLETE: {
			return {
				...state,
				autoSuggestUsers: payload.data
			};
		}

		case LeadsActionType.ERROR_FETCH_LEAD_USER_AUTOCOMPLETE: {
			return {
				...state,
				autoSuggestUsersError: payload.error
			};
		}

		case LeadsActionType.FETCH_LEAD_QUOTATIONS: {
			return {
				...state,
				leadQuotationListLoading: true
			};
		}

		case LeadsActionType.STORE_LEAD_QUOTATIONS: {
			return {
				...state,
				leadQuotationList: payload.data,
				leadQuotationListLoading: false,
				totalRecords: 10
			};
		}

		case LeadsActionType.ERROR_LEAD_QUOTATIONS: {
			return {
				...state,
				leadQuotationListError: payload.error
			};
		}

		case LeadsActionType.FETCH_QUOTATION_LIST: {
			return {
				...state,
				quotationToolListLoading: true
			};
		}

		case LeadsActionType.STORE_QUOTATION_LIST: {
			const normalizedAreas =
				payload?.data?.areas?.map(area => ({
					...area,
					products: (area?.products || []).map((product, idx) => ({
						...product,
						// Backend may omit productOrder; derive it from current order so UI sorting works.
						productOrder: product?.productOrder ?? idx + 1
					}))
				})) || [];

			return {
				...state,
				quotationToolList: normalizedAreas,
				approvalProducts: payload.data.approvalProducts || [],
				canApproveProducts: Boolean(payload.data.canApproveProducts),
				additionalQuotations: payload.data.additionalQuotations || [],
				quotationToolListLocked: payload.data.locked,
				quotationGrandTotal:
					payload.data.grandTotal != null
						? Number(payload.data.grandTotal) || 0
						: state.quotationGrandTotal,
				predictionId: payload.data.predictionId,
				quotationToolListLoading: false,
				totalRecords: 10
			};
		}

		case LeadsActionType.ERROR_QUOTATION_LIST: {
			return {
				...state,
				quotationToolListError: payload.error,
				quotationToolListLoading: false
			};
		}

		case LeadsActionType.FETCH_QUOTATION_LIST_BACKGROUND: {
			return {
				...state
				// No loading state change for background fetch
			};
		}

		case LeadsActionType.STORE_QUOTATION_LIST_BACKGROUND: {
			const normalizedAreas =
				payload?.data?.areas?.map(area => ({
					...area,
					products: (area?.products || []).map((product, idx) => ({
						...product,
						// Backend may omit productOrder; derive it from current order so UI sorting works.
						productOrder: product?.productOrder ?? idx + 1
					}))
				})) || [];

			return {
				...state,
				quotationToolList: normalizedAreas,
				approvalProducts: payload.data.approvalProducts || [],
				canApproveProducts: Boolean(payload.data.canApproveProducts),
				additionalQuotations: payload.data.additionalQuotations || state.additionalQuotations || [],
				quotationToolListLocked: payload.data.locked,
				quotationGrandTotal:
					payload.data.grandTotal != null
						? Number(payload.data.grandTotal) || 0
						: state.quotationGrandTotal,
				predictionId: payload.data.predictionId,
				// No loading state change for background fetch
			};
		}

		case LeadsActionType.ERROR_QUOTATION_LIST_BACKGROUND: {
			return {
				...state
				// No error state change visible to user
			};
		}

		case LeadsActionType.REFRESH_QUOTATION_COSTING: {
			return {
				...state,
				refreshCostingLoading: true,
				refreshCostingStatus: 'PROCESSING',
				refreshCostingError: null
			};
		}

		case LeadsActionType.STORE_REFRESH_QUOTATION_COSTING_STATUS: {
			return {
				...state,
				refreshCostingLoading: payload.status === 'PROCESSING',
				refreshCostingStatus: payload.status,
				refreshCostingError: null
			};
		}

		case LeadsActionType.REFRESH_QUOTATION_COSTING_FAILED: {
			return {
				...state,
				refreshCostingLoading: false,
				refreshCostingStatus: 'FAILED',
				refreshCostingError: payload?.error || null
			};
		}

		case LeadsActionType.FETCH_COSTING_SYNC_STATUS: {
			return {
				...state,
				costingSyncStatusLoading: true
			};
		}

		case LeadsActionType.STORE_COSTING_SYNC_STATUS: {
			return {
				...state,
				costingSyncStatusLoading: false,
				needsRecalculate: Boolean(payload?.needsRecalculate),
				costingSyncDriftCount: payload?.driftCount ?? 0,
				costingSyncSkippedCount: payload?.skippedCount ?? 0
			};
		}

		case LeadsActionType.ERROR_COSTING_SYNC_STATUS: {
			return {
				...state,
				costingSyncStatusLoading: false,
				needsRecalculate: false,
				costingSyncDriftCount: 0,
				costingSyncSkippedCount: 0
			};
		}

		case LeadsActionType.FETCH_BUILDER_FC_DATA: {
			return {
				...state,
				fcBuilderDataLoading: true,
				fcBuilderData: null
			};
		}

		case LeadsActionType.STORE_BUILDER_FC_DATA: {
			return {
				...state,
				fcBuilderDataLoading: false,
				fcBuilderData: payload
			};
		}

		case LeadsActionType.FETCH_LEAD_CREDITS: {
			return {
				...state,
				leadCreditsLoading: true
			};
		}

		case LeadsActionType.STORE_LEAD_CREDITS: {
			return {
				...state,
				leadCreditsLoading: false,
				leadCredits: payload
			};
		}

		case LeadsActionType.ERROR_LEAD_CREDITS: {
			return {
				...state,
				leadCreditsLoading: false
			};
		}

		case LeadsActionType.FETCH_LEAD_SOURCE_HISTORY: {
			return {
				...state,
				leadSourceHistoryLoading: true,
				leadSourceHistoryError: null
			};
		}

		case LeadsActionType.STORE_LEAD_SOURCE_HISTORY: {
			return {
				...state,
				leadSourceHistoryLoading: false,
				leadSourceHistory: payload.data?.history || []
			};
		}

		case LeadsActionType.ERROR_FETCH_LEAD_SOURCE_HISTORY: {
			return {
				...state,
				leadSourceHistoryLoading: false,
				leadSourceHistoryError: payload.error
			};
		}

		case LeadsActionType.FETCH_LEAD_REMARKS: {
			return {
				...state,
				leadRemarksLoading: true,
				leadRemarksError: null
			};
		}

		case LeadsActionType.STORE_LEAD_REMARKS: {
			return {
				...state,
				leadRemarksLoading: false,
				leadRemarks: payload.data ?? []
			};
		}

		case LeadsActionType.ERROR_FETCH_LEAD_REMARKS: {
			return {
				...state,
				leadRemarksLoading: false,
				leadRemarksError: payload.error
			};
		}

		default:
			return state;
	}
};
